import { useMemo, useRef, useState } from "react";
import type { PersonalizedFertilizerPdpResponse } from "@halm-club/halm-shared";
import { FaqListItem } from "../../../../shared/pdp/components/FaqListItem";
import { LoadingSpinner } from "../../../../shared/pdp/components/LoadingSpinner";
import { StickyAddToCartBar } from "../../../../shared/pdp/components/StickyAddToCartBar";
import { useStickyAddToCartVisibility } from "../../../../shared/pdp/components/useStickyAddToCartVisibility";
import { readBooleanHalmParameter } from "../../../../shared/pdp/utils/halmParameters";
import { formatMoney } from "../../../../shared/pdp/utils/pricing";
import { SoilAnalysisComparisonModal } from "./SoilAnalysisComparisonModal";
import "./SoilAnalysisSelector.css";

declare global {
  interface Window {
    openModal?: (modalId: string) => void;
  }
}

const SOIL_ANALYSIS_DETAILS_MODAL_ID = "fertilizer-soil-analysis-details-modal";

type SoilAnalysisVariant =
  PersonalizedFertilizerPdpResponse["soilAnalysisVariants"][number];
type ProductInformationItem =
  PersonalizedFertilizerPdpResponse["collapsibleInformation"][number];

type SoilAnalysisSelectorProps = {
  buttonSubText?: string;
  compareAtPrice: number;
  informationItems: ProductInformationItem[];
  isAddingToCart: boolean;
  isSkipVisible: boolean;
  onAddToCart: () => void;
  onSkip: () => void;
  onTypeChange: (type: "AI" | "LAB") => void;
  price: number;
  selectedType: "AI" | "LAB";
  soilAnalysisVariants: SoilAnalysisVariant[];
  statusMessage: string;
};

export function SoilAnalysisSelector({
  buttonSubText,
  compareAtPrice,
  informationItems,
  isAddingToCart,
  isSkipVisible,
  onAddToCart,
  onSkip,
  onTypeChange,
  price,
  selectedType,
  soilAnalysisVariants,
  statusMessage,
}: SoilAnalysisSelectorProps) {
  const [isComparisonModalOpen, setIsComparisonModalOpen] = useState(false);
  const regularButtonRef = useRef<HTMLButtonElement>(null);
  const isStickyAddToCartVisible =
    useStickyAddToCartVisibility(regularButtonRef);
  const isOrganic = useMemo(() => readBooleanHalmParameter("organic"), []);
  const hasSoilAnalysisDetailsModal = useMemo(
    () => Boolean(document.getElementById(SOIL_ANALYSIS_DETAILS_MODAL_ID)),
    [],
  );
  const stickyTitleTags = [
    selectedType === "LAB" ? "Bodenanalyse" : "KI-Analyse",
    ...(isOrganic ? ["Bio"] : []),
  ];
  const buttonLabel = "Zum Warenkorb hinzufügen";
  const selectedAnalysisVariant = soilAnalysisVariants.find(
    (variant) => variant.type === selectedType,
  );
  const isLabSelected = selectedType === "LAB";
  const analysisTitle = isLabSelected ? "Bodenanalyse im Labor" : "KI-Analyse";
  const analysisDescription = isLabSelected
    ? "Ergebnis in 5-7 Tagen · Dünger wird am Folgetag verschickt"
    : "Basiert auf regionalen Bodendaten - dein Dünger wird sofort versendet.";
  const analysisPrice =
    selectedAnalysisVariant?.priceInCents === 0
      ? "Inklusive"
      : selectedAnalysisVariant
        ? `+${formatMoney(selectedAnalysisVariant.priceInCents)}`
        : "–";
  const openSoilAnalysisDetailsModal = () => {
    const modal = document.getElementById(SOIL_ANALYSIS_DETAILS_MODAL_ID);

    if (!modal || typeof window.openModal !== "function") {
      console.warn("The soil analysis details modal is not available.");
      return;
    }

    window.openModal(SOIL_ANALYSIS_DETAILS_MODAL_ID);
  };

  return (
    <div className="personalized-fertilizer-pdp__soil-analysis-part">
      <div className="personalized-fertilizer-divider">
        <div></div>
      </div>
      <section className="personalized-fertilizer-pdp__soil-section">
        <div className="personalized-fertilizer-pdp__soil-selection">
          <h2 className="personalized-fertilizer-pdp__soil-heading">
            Zuerst messen wir deinen Boden – dann stimmen wir deinen Dünger
            darauf ab.
          </h2>

          {selectedAnalysisVariant ? (
            <div
              className="personalized-fertilizer-pdp__soil-card"
              data-analysis-type={selectedType}
              data-test="fertilizer-soil-analysis-selection"
            >
              <span
                aria-hidden="true"
                className="material-symbols-rounded personalized-fertilizer-pdp__soil-icon"
              >
                {isLabSelected ? "science" : "electric_bolt"}
              </span>
              <div className="personalized-fertilizer-pdp__soil-content">
                <div className="personalized-fertilizer-pdp__soil-title-row">
                  <span className="personalized-fertilizer-pdp__soil-title">
                    {analysisTitle}
                  </span>
                  <span className="personalized-fertilizer-pdp__soil-price">
                    {analysisPrice}
                  </span>
                </div>
                <span className="personalized-fertilizer-pdp__soil-description">
                  {analysisDescription}
                </span>
                {isLabSelected && hasSoilAnalysisDetailsModal ? (
                  <button
                    className="personalized-fertilizer-pdp__soil-learn-more"
                    onClick={openSoilAnalysisDetailsModal}
                    type="button"
                  >
                    Mehr über Bodenanalyse
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}

          {isLabSelected ? (
            <p className="personalized-fertilizer-pdp__soil-ai-prompt">
              Ohne Bodenanalyse starten?{" "}
              <button
                className="personalized-fertilizer-pdp__soil-switch-button"
                data-test="fertilizer-open-ai-analysis-modal"
                onClick={() => setIsComparisonModalOpen(true)}
                type="button"
              >
                → Zur KI-Analyse
              </button>
            </p>
          ) : (
            <button
              className="personalized-fertilizer-pdp__soil-switch-button personalized-fertilizer-pdp__soil-switch-button--standalone"
              data-test="fertilizer-select-lab-analysis"
              onClick={() => onTypeChange("LAB")}
              type="button"
            >
              Zur Bodenanalyse wechseln (empfohlen)
            </button>
          )}
        </div>

        <div className="personalized-fertilizer-pdp__actions">
          <button
            className="personalized-fertilizer-pdp__cart-button"
            data-test="fertilizer-open-purchase-modal"
            disabled={isAddingToCart}
            onClick={onAddToCart}
            ref={regularButtonRef}
            type="button"
          >
            {isAddingToCart ? <LoadingSpinner /> : null}
            <span>{buttonLabel}</span>
          </button>
          {buttonSubText ? (
            <p className="react-pdp__onetime-next-step">{buttonSubText}</p>
          ) : null}
          {isSkipVisible ? (
            <button
              className="personalized-fertilizer-pdp__skip-button"
              onClick={onSkip}
              type="button"
            >
              Überspringen
            </button>
          ) : null}
          {statusMessage ? (
            <p className="react-pdp__onetime-error" role="alert">
              {statusMessage}
            </p>
          ) : null}
        </div>

        {informationItems.length ? (
          <div className="react-pdp__bundle-information-desktop">
            <div className="react-pdp__onetime-faq-list">
              {informationItems.map((item, index) => (
                <FaqListItem
                  description={item.description}
                  key={`${item.title}-${index}`}
                  title={item.title}
                />
              ))}
            </div>
          </div>
        ) : null}
      </section>

      {isStickyAddToCartVisible ? (
        <StickyAddToCartBar
          buttonLabel={buttonLabel}
          compareAtPrice={compareAtPrice}
          isAddingToCart={isAddingToCart}
          onAddToCart={onAddToCart}
          onSkip={isSkipVisible ? onSkip : undefined}
          price={price}
          skipLabel="Überspringen"
          title="Halm Pflegeset"
          titleTags={stickyTitleTags}
        />
      ) : null}

      <SoilAnalysisComparisonModal
        isOpen={isComparisonModalOpen}
        onClose={() => setIsComparisonModalOpen(false)}
        onSelectAi={() => onTypeChange("AI")}
        soilAnalysisVariants={soilAnalysisVariants}
      />
    </div>
  );
}
